import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { scheduleAPI, contactsAPI, locationsAPI, standaloneTruckingAPI, eventsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import TruckingWeekAtAGlance from '../components/TruckingWeekAtAGlance';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format, parseISO, startOfWeek, endOfWeek, addDays } from 'date-fns';
import { Truck, Search, Filter, Clock, CheckCircle, Pencil, Save, Plus, Trash2, MapPin, ChevronDown, ChevronUp, FileText, Download, Loader2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown, Calendar, AlertCircle, X, Monitor } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { usePageTitle } from '../hooks/usePageTitle';

const EVENT_STATUS_OPTIONS = [
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'tentative', label: 'Tentative' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const SCHEDULE_STATUS_OPTIONS = [
  { value: 'Tentative', label: 'Tentative' },
  { value: 'Confirmed', label: 'Confirmed' },
];

const getEventStatusColor = (status) => {
  switch (status) {
    case 'inquiry': return 'border-red-500/30 bg-red-500/10 text-red-400';
    case 'tentative': return 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400';
    case 'confirmed': return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400';
    case 'completed': return 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400';
    case 'cancelled': return 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400';
    default: return 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'M/d/yyyy');
  } catch {
    return dateStr;
  }
};

const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  return timeStr;
};

const TruckingSchedule = () => {
  usePageTitle('Trucking');
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('event-trucking');
  const [scheduleItems, setScheduleItems] = useState([]);
  const [standaloneRuns, setStandaloneRuns] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Edit Event Trucking Dialog
  const [editItem, setEditItem] = useState(null);
  const [editTruckAssignments, setEditTruckAssignments] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Standalone Run Dialog
  const [standaloneDialogOpen, setStandaloneDialogOpen] = useState(false);
  const [editingStandalone, setEditingStandalone] = useState(null);
  const [standaloneForm, setStandaloneForm] = useState({
    date: '',
    time: '',
    pickup_location_id: '',
    dropoff_location_id: '',
    truck_assignments: [],
    notes: '',
    status: 'Tentative',
  });
  
  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);
  
  // Route Sheet Dialog
  const [runSheetDialogOpen, setRunSheetDialogOpen] = useState(false);
  const [runSheetDate, setRunSheetDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [previewHTML, setPreviewHTML] = useState(null);
  
  // Get initial filter values from URL params
  const getInitialFilters = () => {
    const params = new URLSearchParams(window.location.search);
    const dateParam = params.get('date');
    if (dateParam) {
      return {
        event_search: '',
        event_status: '',
        status: '',
        start_date: dateParam,
        end_date: dateParam,
        unassigned: false,
      };
    }
    return {
      event_search: '',
      event_status: '',
      status: '',
      start_date: '',
      end_date: '',
      unassigned: false,
    };
  };
  
  const [filters, setFilters] = useState(getInitialFilters);
  const [activePreset, setActivePreset] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('asc');

  // Handle URL query parameters - runs on mount AND when URL params change
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const addParam = searchParams.get('add');
    const exportParam = searchParams.get('export');
    
    if (dateParam) {
      // Update filters to show only that date
      setFilters(prev => ({
        ...prev,
        start_date: dateParam,
        end_date: dateParam,
      }));
      setActivePreset(null);
    }
    
    if (addParam === 'true') {
      // Open the standalone dialog with the date pre-filled
      setStandaloneForm(prev => ({
        ...prev,
        date: dateParam || format(new Date(), 'yyyy-MM-dd'),
      }));
      setStandaloneDialogOpen(true);
    }
    
    if (exportParam === 'true' && dateParam) {
      // Set the run sheet date and open the dialog
      setRunSheetDate(dateParam);
      setRunSheetDialogOpen(true);
    }
    
    // Clear URL params after processing
    if (dateParam || addParam || exportParam) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  // Filter preset definitions
  const getPresetFilters = (preset) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const endOfCurrentWeek = format(endOfWeek(new Date(), { weekStartsOn: 0 }), 'yyyy-MM-dd');
    const next7Days = format(addDays(new Date(), 7), 'yyyy-MM-dd');
    
    switch (preset) {
      case 'today':
        return { start_date: today, end_date: today, unassigned: false };
      case 'this-week':
        return { start_date: today, end_date: endOfCurrentWeek, unassigned: false };
      case 'next-7-days':
        return { start_date: today, end_date: next7Days, unassigned: false };
      case 'unassigned':
        return { start_date: '', end_date: '', unassigned: true };
      default:
        return {};
    }
  };

  const applyPreset = (preset) => {
    if (activePreset === preset) {
      // Clicking the same preset again clears it
      clearFilters();
      return;
    }
    
    const presetFilters = getPresetFilters(preset);
    setFilters({
      event_search: '',
      event_status: '',
      status: '',
      start_date: presetFilters.start_date || '',
      end_date: presetFilters.end_date || '',
      unassigned: presetFilters.unassigned || false,
    });
    setActivePreset(preset);
    setPage(1);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadSchedule();
  }, [page, limit, sortBy, sortOrder]);

  // Trigger search when preset is applied
  useEffect(() => {
    if (activePreset !== null) {
      loadSchedule();
    }
  }, [activePreset]);

  const loadData = async () => {
    try {
      const [scheduleRes, standaloneRes, contactsRes, locationsRes, eventsRes] = await Promise.all([
        scheduleAPI.getTrucking({ page, limit, sort_by: sortBy, sort_order: sortOrder, ...filters }),
        standaloneTruckingAPI.list(),
        contactsAPI.list({ limit: 1000 }),
        locationsAPI.list({ limit: 1000 }),
        eventsAPI.list({ limit: 1000 }),
      ]);
      
      // Handle paginated response format
      if (scheduleRes.data.data && scheduleRes.data.pagination) {
        setScheduleItems(scheduleRes.data.data);
        setPagination(scheduleRes.data.pagination);
      } else {
        setScheduleItems(scheduleRes.data);
        setPagination({
          total: scheduleRes.data.length,
          total_pages: 1,
          has_next: false,
          has_prev: false
        });
      }
      
      setStandaloneRuns(standaloneRes.data);
      // Handle paginated response format
      setContacts(contactsRes.data.data || contactsRes.data);
      setLocations(locationsRes.data.data || locationsRes.data);
      setEvents(eventsRes.data.data || eventsRes.data);
    } catch (error) {
      toast.error('Failed to load trucking schedule');
    } finally {
      setLoading(false);
    }
  };

  const loadSchedule = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (filters.event_search) params.event_search = filters.event_search;
      if (filters.event_status) params.event_status = filters.event_status;
      if (filters.status) params.status = filters.status;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.unassigned) params.unassigned = true;
      
      const response = await scheduleAPI.getTrucking(params);
      
      // Handle paginated response format
      if (response.data.data && response.data.pagination) {
        setScheduleItems(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setScheduleItems(response.data);
        setPagination({
          total: response.data.length,
          total_pages: 1,
          has_next: false,
          has_prev: false
        });
      }
    } catch (error) {
      toast.error('Failed to load trucking schedule');
    } finally {
      setLoading(false);
    }
  };

  const loadStandaloneRuns = async () => {
    try {
      const response = await standaloneTruckingAPI.list();
      setStandaloneRuns(response.data);
    } catch (error) {
      toast.error('Failed to load standalone runs');
    }
  };

  const handleSearch = () => {
    setActivePreset(null); // Clear preset when manual search is used
    setPage(1); // Reset to first page on search
    loadSchedule();
  };

  const clearFilters = () => {
    setFilters({
      event_search: '',
      event_status: '',
      status: '',
      start_date: '',
      end_date: '',
      unassigned: false,
    });
    setActivePreset(null);
    setPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const renderPagination = () => {
    const { total, total_pages, has_next, has_prev } = pagination;
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, page - Math.floor(maxVisible / 2));
      let end = Math.min(total_pages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-border/50">
        <div className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{total > 0 ? startItem : 0}</span> to{' '}
          <span className="font-medium text-foreground">{endItem}</span> of{' '}
          <span className="font-medium text-foreground">{total}</span> trucking items
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows:</span>
            <Select value={limit.toString()} onValueChange={(val) => { setLimit(parseInt(val)); setPage(1); }}>
              <SelectTrigger className="w-16 h-8 bg-background/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(1)}
              disabled={!has_prev}
              data-testid="trucking-first-page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(page - 1)}
              disabled={!has_prev}
              data-testid="trucking-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-1">
              {getPageNumbers().map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage(pageNum)}
                  data-testid={`trucking-page-${pageNum}`}
                >
                  {pageNum}
                </Button>
              ))}
            </div>

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(page + 1)}
              disabled={!has_next}
              data-testid="trucking-next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(total_pages)}
              disabled={!has_next}
              data-testid="trucking-last-page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const toggleRowExpanded = (itemId) => {
    setExpandedRows(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Event Trucking Edit
  const openEditDialog = (item) => {
    setEditItem(item);
    // Initialize with existing truck_assignments or create from legacy fields
    const assignments = item.truck_assignments && item.truck_assignments.length > 0
      ? item.truck_assignments
      : item.driver_id || item.truck
        ? [{ driver_id: item.driver_id || '', driver_name: item.driver_name || '', truck: item.truck || '', notes: item.driver_notes || '' }]
        : [{ driver_id: '', driver_name: '', truck: '', notes: '' }];
    setEditTruckAssignments(assignments);
  };

  const addTruckAssignment = () => {
    setEditTruckAssignments([...editTruckAssignments, { driver_id: '', driver_name: '', truck: '', notes: '' }]);
  };

  const removeTruckAssignment = (index) => {
    if (editTruckAssignments.length > 1) {
      setEditTruckAssignments(editTruckAssignments.filter((_, i) => i !== index));
    }
  };

  const updateTruckAssignment = (index, field, value) => {
    const newAssignments = [...editTruckAssignments];
    newAssignments[index] = { ...newAssignments[index], [field]: value };
    // Auto-populate driver name when driver_id changes
    if (field === 'driver_id') {
      const contact = contacts.find(c => c.id === value);
      newAssignments[index].driver_name = contact ? contact.contact_name : '';
    }
    setEditTruckAssignments(newAssignments);
  };

  const handleSaveEventTrucking = async () => {
    if (!editItem) return;
    
    setSaving(true);
    try {
      await scheduleAPI.updateTrucking(editItem.event_id, editItem.id, {
        truck_assignments: editTruckAssignments.filter(a => a.driver_id || a.truck)
      });
      toast.success('Trucking details updated');
      setEditItem(null);
      loadSchedule();
    } catch (error) {
      toast.error('Failed to update trucking details');
    } finally {
      setSaving(false);
    }
  };

  // Standalone Run Functions
  const openStandaloneDialog = (run = null) => {
    if (run) {
      setEditingStandalone(run);
      setStandaloneForm({
        date: run.date || '',
        time: run.time || '',
        pickup_location_id: run.pickup_location_id || '',
        dropoff_location_id: run.dropoff_location_id || '',
        truck_assignments: run.truck_assignments?.length > 0 
          ? run.truck_assignments 
          : [{ driver_id: '', driver_name: '', truck: '', notes: '' }],
        notes: run.notes || '',
        status: run.status || 'Tentative',
      });
    } else {
      setEditingStandalone(null);
      setStandaloneForm({
        date: '',
        time: '',
        pickup_location_id: '',
        dropoff_location_id: '',
        truck_assignments: [{ driver_id: '', driver_name: '', truck: '', notes: '' }],
        notes: '',
        status: 'Tentative',
      });
    }
    setStandaloneDialogOpen(true);
  };

  const addStandaloneAssignment = () => {
    setStandaloneForm({
      ...standaloneForm,
      truck_assignments: [...standaloneForm.truck_assignments, { driver_id: '', driver_name: '', truck: '', notes: '' }]
    });
  };

  const removeStandaloneAssignment = (index) => {
    if (standaloneForm.truck_assignments.length > 1) {
      setStandaloneForm({
        ...standaloneForm,
        truck_assignments: standaloneForm.truck_assignments.filter((_, i) => i !== index)
      });
    }
  };

  const updateStandaloneAssignment = (index, field, value) => {
    const newAssignments = [...standaloneForm.truck_assignments];
    newAssignments[index] = { ...newAssignments[index], [field]: value };
    if (field === 'driver_id') {
      const contact = contacts.find(c => c.id === value);
      newAssignments[index].driver_name = contact ? contact.contact_name : '';
    }
    setStandaloneForm({ ...standaloneForm, truck_assignments: newAssignments });
  };

  const handleSaveStandalone = async () => {
    if (!standaloneForm.date) {
      toast.error('Date is required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...standaloneForm,
        truck_assignments: standaloneForm.truck_assignments.filter(a => a.driver_id || a.truck)
      };

      if (editingStandalone) {
        await standaloneTruckingAPI.update(editingStandalone.id, data);
        toast.success('Trucking run updated');
      } else {
        await standaloneTruckingAPI.create(data);
        toast.success('Trucking run created');
      }
      setStandaloneDialogOpen(false);
      loadStandaloneRuns();
    } catch (error) {
      toast.error('Failed to save trucking run');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStandalone = async () => {
    if (!deleteId) return;
    try {
      await standaloneTruckingAPI.delete(deleteId);
      toast.success('Trucking run deleted');
      loadStandaloneRuns();
    } catch (error) {
      toast.error('Failed to delete trucking run');
    } finally {
      setDeleteId(null);
    }
  };

  const getLocationName = (id) => {
    const location = locations.find(l => l.id === id);
    return location ? location.location_name : '-';
  };

  // Get all unique drivers from schedule items and standalone runs for a specific date
  const getDriversForDate = (date) => {
    const drivers = new Map();
    
    // From event trucking
    scheduleItems
      .filter(item => item.date === date)
      .forEach(item => {
        const assignments = item.truck_assignments || [];
        if (assignments.length > 0) {
          assignments.forEach(a => {
            if (a.driver_id && a.driver_name) {
              if (!drivers.has(a.driver_id)) {
                drivers.set(a.driver_id, { id: a.driver_id, name: a.driver_name });
              }
            }
          });
        } else if (item.driver_id && item.driver_name) {
          if (!drivers.has(item.driver_id)) {
            drivers.set(item.driver_id, { id: item.driver_id, name: item.driver_name });
          }
        }
      });
    
    // From standalone runs
    standaloneRuns
      .filter(run => run.date === date)
      .forEach(run => {
        const assignments = run.truck_assignments || [];
        assignments.forEach(a => {
          if (a.driver_id && a.driver_name) {
            if (!drivers.has(a.driver_id)) {
              drivers.set(a.driver_id, { id: a.driver_id, name: a.driver_name });
            }
          }
        });
      });
    
    return Array.from(drivers.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Helper to get full location info
  const getLocationInfo = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    if (!location) return { name: '-', address: '-' };
    // Use 'address' field (primary) or 'street_address' as fallback
    const streetAddr = location.address || location.street_address;
    const address = [
      streetAddr,
      location.city,
      location.state,
      location.zip_code
    ].filter(Boolean).join(', ');
    return {
      name: location.location_name || '-',
      address: address || '-'
    };
  };

  // Helper to get event info including PM
  const getEventInfo = (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return { locationInfo: { name: '-', address: '-' }, pmName: null, pmPhone: null };
    
    const locationInfo = getLocationInfo(event.location_id);
    
    // Get PM contact info
    let pmName = event.project_manager_name || null;
    let pmPhone = null;
    if (event.project_manager_id) {
      const pmContact = contacts.find(c => c.id === event.project_manager_id);
      if (pmContact) {
        pmPhone = pmContact.mobile || pmContact.phone || null;
      }
    }
    
    return { locationInfo, pmName, pmPhone };
  };

  // Get runs for a specific date (both event and standalone)
  const getRunsForDate = (date, driverId = null) => {
    const runs = [];
    
    // Event trucking items
    scheduleItems
      .filter(item => item.date === date)
      .forEach(item => {
        const eventInfo = getEventInfo(item.event_id);
        const assignments = item.truck_assignments || [];
        
        const baseRunData = {
          type: 'event',
          time: item.time,
          event_name: item.event_name,
          flex_number: item.flex_number,
          action: item.action,
          location_name: eventInfo.locationInfo.name,
          location_address: eventInfo.locationInfo.address,
          pm_name: eventInfo.pmName,
          pm_phone: eventInfo.pmPhone,
        };
        
        if (driverId) {
          // Filter to specific driver
          const driverAssignment = assignments.find(a => a.driver_id === driverId);
          if (driverAssignment) {
            runs.push({
              ...baseRunData,
              driver_name: driverAssignment.driver_name,
              truck: driverAssignment.truck,
              notes: driverAssignment.notes || item.notes,
            });
          } else if (!assignments.length && item.driver_id === driverId) {
            runs.push({
              ...baseRunData,
              driver_name: item.driver_name,
              truck: item.truck,
              notes: item.driver_notes || item.notes,
            });
          }
        } else {
          // All runs
          if (assignments.length > 0) {
            assignments.forEach(a => {
              runs.push({
                ...baseRunData,
                driver_name: a.driver_name,
                truck: a.truck,
                notes: a.notes || item.notes,
              });
            });
          } else {
            runs.push({
              ...baseRunData,
              driver_name: item.driver_name,
              truck: item.truck,
              notes: item.driver_notes || item.notes,
            });
          }
        }
      });
    
    // Standalone runs
    standaloneRuns
      .filter(run => run.date === date)
      .forEach(run => {
        const pickupInfo = getLocationInfo(run.pickup_location_id);
        const dropoffInfo = getLocationInfo(run.dropoff_location_id);
        const assignments = run.truck_assignments || [];
        
        const baseRunData = {
          type: 'standalone',
          time: run.time,
          pickup_name: run.pickup_location_name || pickupInfo.name,
          pickup_address: pickupInfo.address,
          dropoff_name: run.dropoff_location_name || dropoffInfo.name,
          dropoff_address: dropoffInfo.address,
        };
        
        if (driverId) {
          const driverAssignment = assignments.find(a => a.driver_id === driverId);
          if (driverAssignment) {
            runs.push({
              ...baseRunData,
              driver_name: driverAssignment.driver_name,
              truck: driverAssignment.truck,
              notes: driverAssignment.notes || run.notes,
            });
          }
        } else {
          if (assignments.length > 0) {
            assignments.forEach(a => {
              runs.push({
                ...baseRunData,
                driver_name: a.driver_name,
                truck: a.truck,
                notes: a.notes || run.notes,
              });
            });
          } else {
            runs.push({
              ...baseRunData,
              driver_name: null,
              truck: null,
              notes: run.notes,
            });
          }
        }
      });
    
    // Sort by time
    return runs.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  };

  // Generate HTML content for route sheets
  const generateRunSheetHTML = (driverId = null) => {
    const formattedDate = format(parseISO(runSheetDate), 'EEEE, MMMM d, yyyy');
    const runs = getRunsForDate(runSheetDate, driverId);
    
    if (runs.length === 0) {
      return null;
    }

    const driverName = driverId ? runs[0]?.driver_name : null;
    
    // Build table rows - separate rows for each run with detailed location info
    const tableRows = runs.map((run, idx) => {
      // Build location cell content
      let locationContent = '';
      if (run.type === 'event') {
        locationContent = `
          <strong style="color:#333;">${run.location_name || '-'}</strong><br/>
          <span style="font-size: 10px; color: #666;">${run.location_address || ''}</span>
        `;
      } else {
        locationContent = `
          <div style="margin-bottom: 4px;">
            <span style="color: #22c55e; font-weight: bold;">Pickup:</span> ${run.pickup_name || '-'}<br/>
            <span style="font-size: 10px; color: #666;">${run.pickup_address || ''}</span>
          </div>
          <div>
            <span style="color: #ef4444; font-weight: bold;">Dropoff:</span> ${run.dropoff_name || '-'}<br/>
            <span style="font-size: 10px; color: #666;">${run.dropoff_address || ''}</span>
          </div>
        `;
      }
      
      // Build PM info for event runs
      let pmContent = '';
      if (run.type === 'event' && run.pm_name) {
        pmContent = `
          <div style="margin-top: 4px; padding-top: 4px; border-top: 1px dashed #ddd;">
            <span style="font-size: 10px; color: #666;">PM:</span> 
            <strong style="color:#333;">${run.pm_name}</strong>
            ${run.pm_phone ? `<br/><span style="font-size: 10px; color: #666;">${run.pm_phone}</span>` : ''}
          </div>
        `;
      }
      
      return `
        <tr style="background-color: ${idx % 2 === 0 ? '#fff' : '#f5f5f5'}; color: #333; vertical-align: top;">
          <td style="padding: 8px; border: 1px solid #ddd; font-weight: bold; color: #333; white-space: nowrap;">${run.time || '-'}</td>
          <td style="padding: 8px; border: 1px solid #ddd; color: #333;">
            ${driverName 
              ? (run.type === 'event' 
                  ? '<strong style="color:#333;">' + (run.event_name || '-') + '</strong>' + (run.flex_number ? '<br/><span style="font-size:10px;color:#666;">#' + run.flex_number + '</span>' : '')
                  : '<strong style="color:#333;">Standalone Run</strong>')
              : '<strong style="color:#333;">' + (run.driver_name || 'Unassigned') + '</strong>'}
          </td>
          ${driverName ? '' : '<td style="padding: 8px; border: 1px solid #ddd; color: #333;">' + (run.truck || '-') + '</td>'}
          <td style="padding: 8px; border: 1px solid #ddd; color: #333;">
            ${run.type === 'event' ? (run.action || '-') : '-'}
          </td>
          <td style="padding: 8px; border: 1px solid #ddd; color: #333;">
            ${locationContent}
            ${pmContent}
          </td>
          <td style="padding: 8px; border: 1px solid #ddd; color: #333;">${run.notes || '-'}</td>
        </tr>
      `;
    }).join('');
    
    return `
      <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 900px; margin: 0 auto; background: white;">
        <!-- Header -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #f97316; padding-bottom: 15px; margin-bottom: 20px;">
          <div>
            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="font-size: 28px; font-weight: bold; color: #f97316; letter-spacing: 2px; font-family: 'Arial Black', Arial, sans-serif;">AV</span>
              <span style="font-size: 22px; font-weight: 300; color: #333;">FACTORY</span>
            </div>
            <p style="margin: 5px 0 0 0; font-size: 12px; color: #666;">Professional AV Production</p>
            <p style="margin: 2px 0 0 0; font-size: 12px; color: #666;">(206) 763-7181</p>
          </div>
          <div style="text-align: right;">
            <h2 style="margin: 0; font-size: 18px; color: #333;">${driverName ? 'Driver Route Sheet' : 'Daily Trucking Summary'}</h2>
            <p style="margin: 5px 0 0 0; font-size: 14px; color: #666;">${formattedDate}</p>
            ${driverName ? `<p style="margin: 5px 0 0 0; font-size: 16px; font-weight: bold; color: #f97316;">${driverName}</p>` : ''}
          </div>
        </div>
        
        <!-- Runs Table -->
        <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
          <thead>
            <tr style="background-color: #f97316; color: white;">
              <th style="padding: 8px; text-align: left; border: 1px solid #ddd; width: 60px;">Time</th>
              <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">${driverName ? 'Event/Run' : 'Driver'}</th>
              ${driverName ? '' : '<th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Truck</th>'}
              <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Action</th>
              <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Location / Contact</th>
              <th style="padding: 8px; text-align: left; border: 1px solid #ddd;">Notes</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        
        <!-- Footer -->
        <div style="margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 10px; color: #666; text-align: center;">
          <p>Generated on ${format(new Date(), 'MMMM d, yyyy h:mm a')} | AV Factory • (206) 763-7181</p>
        </div>
      </div>
    `;
  };

  // Preview the route sheet
  const previewRunSheet = (driverId = null) => {
    const html = generateRunSheetHTML(driverId);
    if (!html) {
      toast.error('No runs found for this date');
      return;
    }
    setPreviewHTML({ html, driverId });
  };

  // Generate PDF for daily summary or driver-specific
  const generateRunSheetPDF = async (driverId = null) => {
    setGeneratingPDF(true);
    try {
      const html = generateRunSheetHTML(driverId);
      
      if (!html) {
        toast.error('No runs found for this date');
        setGeneratingPDF(false);
        return;
      }

      const driverName = driverId ? getRunsForDate(runSheetDate, driverId)[0]?.driver_name : null;

      // Create container and render to PDF
      const container = document.createElement('div');
      container.innerHTML = html;
      // Make it visible but behind everything to get proper height calculation
      container.style.cssText = 'position: absolute; left: 0; top: 0; width: 900px; min-height: 600px; background: white; z-index: -9999; pointer-events: none;';
      document.body.appendChild(container);
      
      // Wait for render
      await new Promise(resolve => setTimeout(resolve, 300));

      const filename = driverName 
        ? `RouteSheet_${driverName.replace(/\s+/g, '_')}_${runSheetDate}.pdf`
        : `TruckingSummary_${runSheetDate}.pdf`;

      const worker = html2pdf()
        .set({
          margin: 10,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2, 
            useCORS: true, 
            logging: true, 
            backgroundColor: '#ffffff'
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        })
        .from(container.firstElementChild)
        .toPdf();

      const pdfObj = await worker.get('pdf');
      const pdfBlob = pdfObj.output('blob');
      document.body.removeChild(container);
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Run sheet downloaded: ${filename}`);
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Render truck assignments summary
  const renderTruckAssignments = (item) => {
    const assignments = item.truck_assignments || [];
    if (assignments.length === 0) {
      // Fallback to legacy fields
      if (item.driver_name || item.truck) {
        return (
          <div className="text-sm">
            {item.driver_name && <span className="font-medium">{item.driver_name}</span>}
            {item.truck && <span className="text-muted-foreground ml-2">({item.truck})</span>}
          </div>
        );
      }
      return <span className="text-muted-foreground">No trucks assigned</span>;
    }
    
    if (assignments.length === 1) {
      const a = assignments[0];
      return (
        <div className="text-sm">
          {a.driver_name && <span className="font-medium">{a.driver_name}</span>}
          {a.truck && <span className="text-muted-foreground ml-2">({a.truck})</span>}
        </div>
      );
    }

    return (
      <div className="text-sm">
        <Badge variant="outline" className="border-orange-500/30 bg-orange-500/10 text-orange-400">
          {assignments.length} trucks
        </Badge>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="trucking-schedule">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Truck className="h-8 w-8 text-orange-400" />
            Trucking Schedule
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage event trucking and standalone runs
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => window.open('/dispatch', '_blank')}
            data-testid="dispatch-display-btn"
            title="Open full-screen dispatch display for office monitors"
          >
            <Monitor className="h-4 w-4 mr-2" />
            Dispatch Display
          </Button>
          <Button variant="outline" onClick={() => setRunSheetDialogOpen(true)} data-testid="run-sheets-btn">
            <FileText className="h-4 w-4 mr-2" />
            Export Route Sheets
          </Button>
          <Button onClick={() => openStandaloneDialog()} data-testid="add-standalone-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Standalone Run
          </Button>
        </div>
      </div>

      {/* Week at a Glance */}
      <TruckingWeekAtAGlance />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50">
          <TabsTrigger value="event-trucking">Event Trucking</TabsTrigger>
          <TabsTrigger value="standalone">Standalone Runs</TabsTrigger>
        </TabsList>

        {/* Event Trucking Tab */}
        <TabsContent value="event-trucking" className="space-y-6">
          {/* Filters */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Event Name / Flex #</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={filters.event_search}
                      onChange={(e) => setFilters({ ...filters, event_search: e.target.value })}
                      placeholder="Search events..."
                      className="bg-background/50 pl-10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Event Status</label>
                  <Select
                    value={filters.event_status || 'all'}
                    onValueChange={(value) => setFilters({ ...filters, event_status: value === 'all' ? '' : value })}
                  >
                    <SelectTrigger className="bg-background/50">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {EVENT_STATUS_OPTIONS.map((status) => (
                        <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Status</label>
                  <Select
                    value={filters.status || 'all'}
                    onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
                  >
                    <SelectTrigger className="bg-background/50">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All statuses</SelectItem>
                      {SCHEDULE_STATUS_OPTIONS.map((status) => (
                        <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Start Date</label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                    className="bg-background/50"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">End Date</label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                    className="bg-background/50"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button onClick={handleSearch} data-testid="search-btn">
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Event Trucking Table */}
          <Card className="border-border/50 bg-card/50">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
                </div>
              ) : scheduleItems.length > 0 ? (
                <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50">
                        <TableHead className="w-8"></TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleSort('date')}
                        >
                          <div className="flex items-center">
                            Date
                            {getSortIcon('date')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleSort('time')}
                        >
                          <div className="flex items-center">
                            Time
                            {getSortIcon('time')}
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleSort('event_name')}
                        >
                          <div className="flex items-center">
                            Event
                            {getSortIcon('event_name')}
                          </div>
                        </TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Event Status</TableHead>
                        <TableHead>Trucks/Drivers</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduleItems.map((item) => {
                        const assignments = item.truck_assignments || [];
                        const isExpanded = expandedRows[item.id];
                        const hasMultiple = assignments.length > 1;
                        
                        return (
                          <React.Fragment key={item.id}>
                            <TableRow className="border-border/50 hover:bg-muted/30">
                              <TableCell>
                                {hasMultiple && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => toggleRowExpanded(item.id)}
                                  >
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  </Button>
                                )}
                              </TableCell>
                              <TableCell className="font-medium">{formatDate(item.date)}</TableCell>
                              <TableCell className="font-mono">{formatTime(item.time)}</TableCell>
                              <TableCell>
                                <Link 
                                  to={`/events/${item.event_id}`}
                                  className="hover:text-primary transition-colors"
                                >
                                  {item.event_name}
                                  {item.flex_number && (
                                    <span className="text-muted-foreground text-xs ml-1">#{item.flex_number}</span>
                                  )}
                                </Link>
                              </TableCell>
                              <TableCell>{item.action || '-'}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className={getEventStatusColor(item.event_status)}>
                                  {item.event_status || '-'}
                                </Badge>
                              </TableCell>
                              <TableCell>{renderTruckAssignments(item)}</TableCell>
                              <TableCell className="text-muted-foreground max-w-[150px] truncate">
                                {item.notes || '-'}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditDialog(item)}
                                  data-testid={`edit-trucking-${item.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                            {/* Expanded row showing all truck assignments */}
                            {hasMultiple && isExpanded && assignments.map((assignment, idx) => (
                              <TableRow key={`${item.id}-${idx}`} className="bg-muted/20 border-border/30">
                                <TableCell></TableCell>
                                <TableCell colSpan={2} className="pl-8">
                                  <span className="text-xs text-muted-foreground">Truck {idx + 1}</span>
                                </TableCell>
                                <TableCell>
                                  {assignment.driver_name ? (
                                    <Link to={`/contacts/${assignment.driver_id}`} className="hover:text-primary">
                                      {assignment.driver_name}
                                    </Link>
                                  ) : '-'}
                                </TableCell>
                                <TableCell>{assignment.truck || '-'}</TableCell>
                                <TableCell colSpan={3} className="text-muted-foreground">
                                  {assignment.notes || '-'}
                                </TableCell>
                                <TableCell></TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {renderPagination()}
                </>
              ) : (
                <div className="text-center py-12">
                  <Truck className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No trucking items found</p>
                  <p className="text-sm text-muted-foreground/60 mt-1">
                    Try adjusting your filters or add trucking schedule items to events
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Standalone Runs Tab */}
        <TabsContent value="standalone" className="space-y-6">
          <Card className="border-border/50 bg-card/50">
            <CardContent className="p-0">
              {standaloneRuns.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50">
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Pickup</TableHead>
                        <TableHead>Dropoff</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Trucks/Drivers</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead className="w-24"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {standaloneRuns.map((run) => (
                        <TableRow key={run.id} className="border-border/50 hover:bg-muted/30">
                          <TableCell className="font-medium">{formatDate(run.date)}</TableCell>
                          <TableCell className="font-mono">{formatTime(run.time)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-emerald-400" />
                              {run.pickup_location_name || getLocationName(run.pickup_location_id) || '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-red-400" />
                              {run.dropoff_location_name || getLocationName(run.dropoff_location_id) || '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            {run.status === 'Confirmed' ? (
                              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Confirmed
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="border-yellow-500/30 bg-yellow-500/10 text-yellow-400">
                                <Clock className="h-3 w-3 mr-1" />
                                Tentative
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {run.truck_assignments?.length > 0 ? (
                              run.truck_assignments.length === 1 ? (
                                <span>
                                  {run.truck_assignments[0].driver_name || '-'}
                                  {run.truck_assignments[0].truck && (
                                    <span className="text-muted-foreground ml-1">({run.truck_assignments[0].truck})</span>
                                  )}
                                </span>
                              ) : (
                                <Badge variant="outline" className="border-orange-500/30 bg-orange-500/10 text-orange-400">
                                  {run.truck_assignments.length} trucks
                                </Badge>
                              )
                            ) : '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground max-w-[150px] truncate">
                            {run.notes || '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openStandaloneDialog(run)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteId(run.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Truck className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No standalone trucking runs</p>
                  <p className="text-sm text-muted-foreground/60 mt-1">
                    Create standalone runs for non-event trucking
                  </p>
                  <Button className="mt-4" onClick={() => openStandaloneDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Standalone Run
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Event Trucking Dialog */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Trucking Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <Label>Truck Assignments</Label>
              <Button variant="outline" size="sm" onClick={addTruckAssignment}>
                <Plus className="h-4 w-4 mr-1" />
                Add Truck
              </Button>
            </div>
            
            {editTruckAssignments.map((assignment, index) => (
              <Card key={index} className="border-border/50 bg-card/30">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Truck {index + 1}</span>
                    {editTruckAssignments.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeTruckAssignment(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Driver</Label>
                      <Select
                        value={assignment.driver_id || 'none'}
                        onValueChange={(value) => updateTruckAssignment(index, 'driver_id', value === 'none' ? '' : value)}
                      >
                        <SelectTrigger className="bg-background/50 h-9">
                          <SelectValue placeholder="Select driver" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No driver</SelectItem>
                          {contacts.map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.contact_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Truck</Label>
                      <Input
                        value={assignment.truck || ''}
                        onChange={(e) => updateTruckAssignment(index, 'truck', e.target.value)}
                        placeholder="Truck #"
                        className="bg-background/50 h-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Textarea
                      value={assignment.notes || ''}
                      onChange={(e) => updateTruckAssignment(index, 'notes', e.target.value)}
                      placeholder="Driver notes..."
                      rows={2}
                      className="bg-background/50"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEventTrucking} disabled={saving}>
              {saving ? 'Saving...' : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Standalone Run Dialog */}
      <Dialog open={standaloneDialogOpen} onOpenChange={setStandaloneDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingStandalone ? 'Edit' : 'New'} Standalone Trucking Run</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={standaloneForm.date}
                  onChange={(e) => setStandaloneForm({ ...standaloneForm, date: e.target.value })}
                  className="bg-background/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={standaloneForm.time}
                  onChange={(e) => setStandaloneForm({ ...standaloneForm, time: e.target.value })}
                  className="bg-background/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={standaloneForm.status}
                  onValueChange={(value) => setStandaloneForm({ ...standaloneForm, status: value })}
                >
                  <SelectTrigger className="bg-background/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCHEDULE_STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pickup Location</Label>
                <Select
                  value={standaloneForm.pickup_location_id || 'none'}
                  onValueChange={(value) => setStandaloneForm({ ...standaloneForm, pickup_location_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="bg-background/50">
                    <SelectValue placeholder="Select pickup" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No location</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.location_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Dropoff Location</Label>
                <Select
                  value={standaloneForm.dropoff_location_id || 'none'}
                  onValueChange={(value) => setStandaloneForm({ ...standaloneForm, dropoff_location_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="bg-background/50">
                    <SelectValue placeholder="Select dropoff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No location</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.location_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={standaloneForm.notes}
                onChange={(e) => setStandaloneForm({ ...standaloneForm, notes: e.target.value })}
                placeholder="Run notes..."
                rows={2}
                className="bg-background/50"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Truck Assignments</Label>
              <Button variant="outline" size="sm" onClick={addStandaloneAssignment}>
                <Plus className="h-4 w-4 mr-1" />
                Add Truck
              </Button>
            </div>

            {standaloneForm.truck_assignments.map((assignment, index) => (
              <Card key={index} className="border-border/50 bg-card/30">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Truck {index + 1}</span>
                    {standaloneForm.truck_assignments.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeStandaloneAssignment(index)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Driver</Label>
                      <Select
                        value={assignment.driver_id || 'none'}
                        onValueChange={(value) => updateStandaloneAssignment(index, 'driver_id', value === 'none' ? '' : value)}
                      >
                        <SelectTrigger className="bg-background/50 h-9">
                          <SelectValue placeholder="Select driver" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No driver</SelectItem>
                          {contacts.map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.contact_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Truck</Label>
                      <Input
                        value={assignment.truck || ''}
                        onChange={(e) => updateStandaloneAssignment(index, 'truck', e.target.value)}
                        placeholder="Truck #"
                        className="bg-background/50 h-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Textarea
                      value={assignment.notes || ''}
                      onChange={(e) => updateStandaloneAssignment(index, 'notes', e.target.value)}
                      placeholder="Driver notes..."
                      rows={2}
                      className="bg-background/50"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStandaloneDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveStandalone} disabled={saving}>
              {saving ? 'Saving...' : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {editingStandalone ? 'Save Changes' : 'Create Run'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Trucking Run</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this trucking run? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteStandalone}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Route Sheet Dialog */}
      <Dialog open={runSheetDialogOpen} onOpenChange={(open) => {
        setRunSheetDialogOpen(open);
        if (!open) setPreviewHTML(null);
      }}>
        <DialogContent className={previewHTML ? "max-w-4xl max-h-[90vh] overflow-auto" : "max-w-md"}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-400" />
              {previewHTML ? 'Route Sheet Preview' : 'Generate Route Sheets'}
            </DialogTitle>
          </DialogHeader>
          
          {previewHTML ? (
            <div className="space-y-4">
              <div 
                className="border rounded-lg bg-white p-4 overflow-auto max-h-[60vh]"
                dangerouslySetInnerHTML={{ __html: previewHTML.html }}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPreviewHTML(null)}>
                  Back
                </Button>
                <Button onClick={() => {
                  generateRunSheetPDF(previewHTML.driverId);
                  setPreviewHTML(null);
                }} disabled={generatingPDF}>
                  {generatingPDF ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Download PDF
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Select Date</Label>
                  <Input
                    type="date"
                    value={runSheetDate}
                    onChange={(e) => setRunSheetDate(e.target.value)}
                    className="bg-background/50"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Preview Options</Label>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => previewRunSheet(null)}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Preview Daily Summary (All Drivers)
                    </Button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <FileText className="h-4 w-4 mr-2" />
                          Preview Individual Driver Sheet
                          <ChevronDown className="h-4 w-4 ml-auto" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-56">
                        <DropdownMenuLabel>Select Driver</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {getDriversForDate(runSheetDate).length > 0 ? (
                          getDriversForDate(runSheetDate).map((driver) => (
                            <DropdownMenuItem 
                              key={driver.id}
                              onClick={() => previewRunSheet(driver.id)}
                            >
                              {driver.name}
                            </DropdownMenuItem>
                          ))
                        ) : (
                          <DropdownMenuItem disabled>
                            No drivers found for this date
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                
                <div className="border-t pt-4 space-y-2">
                  <Label className="text-muted-foreground">Direct Download</Label>
                  <div className="space-y-2">
                    <Button 
                      variant="default" 
                      className="w-full justify-start bg-orange-500 hover:bg-orange-600"
                      onClick={() => generateRunSheetPDF(null)}
                      disabled={generatingPDF}
                    >
                      {generatingPDF ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Download Daily Summary
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRunSheetDialogOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TruckingSchedule;
